import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { useAuth } from "@/hooks/useAuth"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useIsAllowedPermission } from "@/hooks/useIsAllowedPermission"
import { useSseStream } from "@/hooks/useSse"
import { Link, useLocation } from "@tanstack/react-router"
import {
  KeyRound,
  LogOut,
  Menu,
  Moon,
  Settings,
  Sun,
  User2,
  X,
} from "lucide-react"
import { useState, type CSSProperties } from "react"
import { useTranslation } from "react-i18next"

import DarkToggle from "../global/dark-toggle"
import LanguageSwitcher from "../global/lang-switcher"
import ThemePalette from "../global/palette"
import { PoolStatus } from "./db-pool-status"
import DevRoleSwitcher from "./DevRoleSwitcher"
import NotificationsDropdown from "./notifications/NotificationsDropdown"
import SupportButton from "./support-button"
import { useApps } from "@/hooks/useApps"
import { cn } from "@/lib/utils"
import { useTheme } from "@/lib/ThemeProvider"

const NAV_IGNORE_LIST = ["stations"]

const Navbar = () => {
  const { isLogged, signOut, user } = useAuth()
  const { t, i18n } = useTranslation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const { theme, setTheme } = useTheme()

  useSseStream()

  const isAllowedToViewPoolStatus = useIsAllowedPermission("view:pool_status")

  const { data: apps = [] } = useApps()

  const isC2Version = import.meta.env.VITE_C2_VERSION === "True"

  const filteredApps = apps.filter(
    (app) => app.nav_bar && !NAV_IGNORE_LIST.includes(app.name),
  )

  const toggleMenu = () => setIsMenuOpen((prev) => !prev)
  const closeMenu = () => setIsMenuOpen(false)

  const handleLogout = () => {
    closeMenu()
    signOut()
  }

  const handleLogin = () => {
    closeMenu()
    window.location.href = "/login"
  }

  const isActiveApp = (name: string) =>
    location.pathname === `/${name}` ||
    location.pathname.startsWith(`/${name}/`)

  if (isC2Version) {
    return (
      <header
        id="app-navbar"
        style={{ "--navbar-height": "73px" } as CSSProperties}
        className="bg-background/95 border-border/40 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 border-b shadow-sm backdrop-blur"
      >
        <div className="flex items-center justify-between px-4 py-3">
          {/* Left Section */}
          <div className="flex items-center gap-6">
            <Link
              to="/"
              className="hover:text-primary flex h-10 items-center justify-center font-bold tracking-[0.12em] transition-colors"
            >
              {import.meta.env.VITE_APP_NAME}
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden items-center gap-1 md:flex">
              {filteredApps.map(({ id, name }) => (
                <Link
                  key={id}
                  // @ts-ignore
                  to="/$name"
                  // @ts-ignore
                  params={{ name }}
                  className={cn(
                    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    isActiveApp(name)
                      ? "text-primary bg-primary/10"
                      : "text-muted-foreground hover:text-foreground hover:bg-accent",
                  )}
                >
                  {t(`navbar.${name}`)}
                </Link>
              ))}
            </nav>
          </div>

          {/* Right Section - Desktop */}

          <div className="hidden items-center gap-2 md:flex">
            <SupportButton />

            {/*{isAllowedToViewPoolStatus && <PoolStatus />}*/}

            <div className="bg-muted/30 flex items-center gap-1.5 rounded-lg px-2 py-1">
              <div
                onClick={() =>
                  i18n.changeLanguage(i18n.language === "en" ? "ua" : "en")
                }
                className="bg-muted/50 relative flex h-6 w-[66px] cursor-pointer items-center rounded-full transition-colors"
              >
                <span
                  className="bg-primary absolute top-0.5 h-5 w-[30px] rounded-full shadow-xs transition-all duration-200"
                  style={{ left: i18n.language === "en" ? "2px" : "34px" }}
                />
                <div className="relative z-10 flex w-full items-center justify-around px-1">
                  <span
                    className={cn(
                      "text-[10px] leading-none font-semibold uppercase",
                      i18n.language === "en"
                        ? "text-primary-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    EN
                  </span>
                  <span
                    className={cn(
                      "text-[10px] leading-none font-semibold uppercase",
                      i18n.language === "ua"
                        ? "text-primary-foreground"
                        : "text-muted-foreground",
                    )}
                  >
                    UA
                  </span>
                </div>
              </div>

              <div className="bg-muted-foreground/15 mx-0.5 h-4 w-px" />

              <button
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className={cn(
                  "relative flex h-6 w-10 cursor-pointer items-center rounded-full transition-colors",
                  theme === "dark" ? "bg-primary/20" : "bg-muted-foreground/20",
                )}
              >
                <span
                  className={cn(
                    "bg-background flex h-4.5 w-4.5 items-center justify-center rounded-full shadow-xs transition-transform",
                    theme === "dark" ? "translate-x-5" : "translate-x-0.5",
                  )}
                >
                  {theme === "dark" ? (
                    <Moon className="h-2.5 w-2.5" />
                  ) : (
                    <Sun className="h-2.5 w-2.5" />
                  )}
                </span>
              </button>
            </div>

            <NotificationsDropdown />

            {isLogged() ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    aria-label="Open user menu"
                    className="focus:ring-ring ml-1 cursor-pointer rounded-full transition-all duration-300 hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                  >
                    <Avatar className="size-8">
                      <AvatarImage
                        src={(user as any)?.profile_image || ""}
                        alt="avatar"
                      />
                      <AvatarFallback>
                        <User2 className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <Avatar className="size-6">
                      <AvatarImage
                        src={(user as any)?.profile_image || ""}
                        alt="avatar"
                      />
                      <AvatarFallback>U</AvatarFallback>
                    </Avatar>
                    <div className="truncate">{user?.username || "User"}</div>
                  </DropdownMenuLabel>

                  <DropdownMenuSeparator />

                  <DropdownMenuGroup>
                    <DropdownMenuItem asChild>
                      <Link to="/station-access" className="cursor-pointer">
                        <KeyRound className="mr-2 h-4 w-4" />
                        {t("stationAccess.title")}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="cursor-pointer">
                        <Settings className="mr-2 h-4 w-4" />
                        {t("nav.settings")}
                      </Link>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>

                  <DevRoleSwitcher />
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer"
                    onSelect={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("logout")}
                  </DropdownMenuItem>
                  <DropdownMenuItem></DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                className="rounded-full px-4"
                onClick={handleLogin}
              >
                {t("login")}
              </Button>
            )}
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={toggleMenu}
            aria-label={isMenuOpen ? "Close menu" : "Open menu"}
          >
            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="fixed inset-x-0 top-[73px] z-[100] md:hidden">
            <div className="bg-background border-border/50 mx-3 rounded-xl border p-4 shadow-xl">
              <div className="flex flex-col gap-4">
                {/* Mobile Controls */}
                <div className="flex flex-wrap items-center gap-2 border-b pb-4">
                  <div className="bg-muted/30 border-border/50 flex items-center gap-1.5 rounded-lg border px-2 py-1">
                    <div
                      onClick={() =>
                        i18n.changeLanguage(
                          i18n.language === "en" ? "ua" : "en",
                        )
                      }
                      className="bg-muted/50 relative flex h-6 w-[66px] cursor-pointer items-center rounded-full transition-colors"
                    >
                      <span
                        className="bg-primary absolute top-0.5 h-5 w-[30px] rounded-full shadow-xs transition-all duration-200"
                        style={{
                          left: i18n.language === "en" ? "2px" : "34px",
                        }}
                      />
                      <div className="relative z-10 flex w-full items-center justify-around px-1">
                        <span
                          className={cn(
                            "text-[10px] leading-none font-semibold uppercase",
                            i18n.language === "en"
                              ? "text-primary-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          EN
                        </span>
                        <span
                          className={cn(
                            "text-[10px] leading-none font-semibold uppercase",
                            i18n.language === "ua"
                              ? "text-primary-foreground"
                              : "text-muted-foreground",
                          )}
                        >
                          UA
                        </span>
                      </div>
                    </div>

                    <div className="bg-muted-foreground/15 mx-0.5 h-4 w-px" />

                    <button
                      onClick={() =>
                        setTheme(theme === "dark" ? "light" : "dark")
                      }
                      className={cn(
                        "relative flex h-6 w-10 cursor-pointer items-center rounded-full transition-colors",
                        theme === "dark"
                          ? "bg-primary/20"
                          : "bg-muted-foreground/20",
                      )}
                    >
                      <span
                        className={cn(
                          "bg-background flex h-4.5 w-4.5 items-center justify-center rounded-full shadow-xs transition-transform",
                          theme === "dark"
                            ? "translate-x-5"
                            : "translate-x-0.5",
                        )}
                      >
                        {theme === "dark" ? (
                          <Moon className="h-2.5 w-2.5" />
                        ) : (
                          <Sun className="h-2.5 w-2.5" />
                        )}
                      </span>
                    </button>
                  </div>
                  <SupportButton
                    dropdownClassName="z-[150]"
                    dialogClassName="z-[150]"
                  />
                  <NotificationsDropdown contentClassName="z-[150]" />
                </div>

                {/* Apps Navigation - Mobile */}
                <nav className="flex flex-col gap-1">
                  {filteredApps.map(({ id, name }) => (
                    <Link
                      key={id}
                      // @ts-ignore
                      to="/$name"
                      // @ts-ignore
                      params={{ name }}
                      onClick={closeMenu}
                      className={cn(
                        "block rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                        isActiveApp(name)
                          ? "text-primary bg-primary/10"
                          : "hover:bg-accent hover:text-accent-foreground",
                      )}
                    >
                      {t(`navbar.${name}`)}
                    </Link>
                  ))}
                </nav>

                {/* Auth Actions */}
                <div className="border-t pt-4">
                  {isLogged() ? (
                    <div className="flex flex-col gap-2">
                      <Button variant="ghost" className="justify-start" asChild>
                        <Link to="/station-access" onClick={closeMenu}>
                          <KeyRound className="mr-2 h-4 w-4" />
                          {t("stationAccess.shortTitle")}
                        </Link>
                      </Button>
                      <Button variant="ghost" className="justify-start" asChild>
                        <Link to="/settings" onClick={closeMenu}>
                          <Settings className="mr-2 h-4 w-4" />
                          {t("nav.settings")}
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        className="justify-start"
                        onClick={handleLogout}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        {t("logout")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="default"
                      className="w-full"
                      onClick={handleLogin}
                    >
                      {t("login")}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </header>
    )
  }

  return (
    <header className="bg-background sticky top-0 z-50">
      <div className="flex items-center justify-between rounded-lg p-2">
        {/* Left Section */}
        <div className="flex items-center gap-5">
          <Link
            to="/"
            className="hover:text-primary flex h-14 w-28 items-center justify-center font-bold tracking-[0.12em] transition-colors"
          >
            {import.meta.env.VITE_APP_NAME || "SPECTER"}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden items-center gap-5 md:flex">
            <div className="flex items-center gap-2 border-r pr-4">
              <LanguageSwitcher />
              <DarkToggle />
              <ThemePalette />
            </div>

            {/* Apps from useApps - Desktop */}
            {apps
              .filter(
                (app) => app.nav_bar && !NAV_IGNORE_LIST.includes(app.name),
              )
              .map(({ id, name }) => (
                <Link
                  key={id} // @ts-ignore
                  to="/$name" // @ts-ignore
                  params={{ name }}
                  className="hover:text-primary text-sm font-medium transition-colors"
                >
                  {t(`navbar.${name}`)}
                </Link>
              ))}
          </nav>
        </div>

        {/* Right Section - Desktop */}
        <div className="hidden items-center gap-3 md:flex">
          {isAllowedToViewPoolStatus && (
            <div className="mr-8">
              <PoolStatus />
            </div>
          )}

          <SupportButton />
          <NotificationsDropdown />

          {isLogged() ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  aria-label="Open user menu"
                  className="focus:ring-ring cursor-pointer rounded-full transition-all duration-300 hover:scale-105 focus:ring-2 focus:ring-offset-2 focus:outline-none"
                >
                  <Avatar className="size-8">
                    <AvatarImage
                      src={(user as any)?.profile_image || ""}
                      alt="avatar"
                    />
                    <AvatarFallback>
                      <User2 className="h-5 w-5" />
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex items-center gap-2">
                  <Avatar className="size-6">
                    <AvatarImage
                      src={(user as any)?.profile_image || ""}
                      alt="avatar"
                    />
                    <AvatarFallback>U</AvatarFallback>
                  </Avatar>
                  <div className="truncate">{user?.username || "User"}</div>
                </DropdownMenuLabel>

                <DropdownMenuSeparator />

                <DropdownMenuGroup>
                  <DropdownMenuItem asChild>
                    <Link to="/station-access" className="cursor-pointer">
                      <KeyRound className="mr-2 h-4 w-4" />
                      {t("stationAccess.title")}
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/settings" className="cursor-pointer">
                      <Settings className="mr-2 h-4 w-4" />
                      {t("nav.settings")}
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuGroup>

                <DevRoleSwitcher />
                <DropdownMenuSeparator />

                <DropdownMenuItem
                  className="cursor-pointer"
                  onSelect={handleLogout}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full px-4"
              onClick={handleLogin}
            >
              {t("login")}
            </Button>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={toggleMenu}
          aria-label={isMenuOpen ? "Close menu" : "Open menu"}
        >
          {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </Button>
      </div>

      {isMenuOpen && (
        <div className="bg-background absolute top-[60px] right-0 left-0 z-[100] rounded-b-lg p-4 shadow-lg md:hidden">
          <div className="flex flex-col gap-4">
            {/* Mobile Controls */}
            <div className="flex flex-wrap items-center gap-3 border-b pb-4">
              <LanguageSwitcher dropdownClassName="z-[150]" />
              <DarkToggle />
              <ThemePalette popoverClassName="z-[150]" />
              <SupportButton
                dropdownClassName="z-[150]"
                dialogClassName="z-[150]"
              />
              <NotificationsDropdown contentClassName="z-[150]" />
            </div>

            {/* Apps Navigation - Mobile (Same approach as desktop) */}
            <nav className="flex flex-col gap-1">
              {apps
                .filter(
                  (app) => app.nav_bar && !NAV_IGNORE_LIST.includes(app.name),
                )
                .map(({ id, name }) => (
                  <Link
                    key={id} // @ts-ignore
                    to="/$name" // @ts-ignore
                    params={{ name }}
                    onClick={closeMenu}
                    className="hover:bg-accent hover:text-accent-foreground block rounded-md px-3 py-2.5 text-sm font-medium transition-colors"
                  >
                    {t(`navbar.${name}`)}
                  </Link>
                ))}
            </nav>

            {/* Auth Actions */}
            <div className="mt-4 border-t pt-4">
              {isLogged() ? (
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    className="flex-1 justify-start"
                    asChild
                  >
                    <Link to="/station-access" onClick={closeMenu}>
                      <KeyRound className="mr-2 h-4 w-4" />
                      {t("stationAccess.shortTitle")}
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    className="flex-1 justify-start"
                    asChild
                  >
                    <Link to="/settings" onClick={closeMenu}>
                      <Settings className="mr-2 h-4 w-4" />
                      {t("nav.settings")}
                    </Link>
                  </Button>

                  <Button
                    variant="ghost"
                    className="flex-1 justify-start"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    {t("logout")}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="default"
                  className="w-full"
                  onClick={handleLogin}
                >
                  {t("login")}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  )
}

export default Navbar
